/**
 * Brings a part of a page into view when a link arrives at it by its #id.
 *
 * The page above is often still filling in when the link lands (the garden's
 * week arrives after the forecast) and pushes the part down, so it is
 * followed as the page grows, until the gardener scrolls or taps, or for a
 * few seconds. Only a new link, or the part becoming ready, starts it again.
 * The same way the frost card does it (FrostOutlook).
 *
 * Who uses it: features/locations LocationGrid (/beds#location-12; a plant's
 * card, the seed-packet scan and Seasons open the bed itself, /beds/12, since
 * D56) and features/weather
 * WeatherPage (/weather#forecast, from Today's Coming up).
 *
 * @module useArrivalScroll
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { ARRIVAL_SCROLL_FOLLOW_MS } from '@/config/tunables/shared';

/** How long to keep following a part as the page above it fills in. */
const FOLLOW_MS = ARRIVAL_SCROLL_FOLLOW_MS;

/**
 * Scrolls to the element with this id when the address's #hash names it.
 *
 * @param id - The element's id, without the #.
 * @param ready - Whether the element is drawn yet.
 */
export function useArrivalScroll(id: string, ready: boolean): void {
  const { hash } = useLocation();
  const arrived = hash === `#${id}`;

  React.useEffect(() => {
    if (!arrived || !ready || typeof ResizeObserver === 'undefined') return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let first = true;
    /** Brings it to the top of the screen: gently the first time, at once after. */
    const bring = (): void => {
      document.getElementById(id)?.scrollIntoView({ behavior: first && !reduce ? 'smooth' : 'auto', block: 'start' });
      first = false;
    };
    const observer = new ResizeObserver(bring);
    const inputs = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    /** Stops following: the gardener has taken over, or the page has settled. */
    const stop = (): void => {
      observer.disconnect();
      inputs.forEach((name) => window.removeEventListener(name, stop));
    };
    observer.observe(document.body);
    inputs.forEach((name) => window.addEventListener(name, stop, { passive: true }));
    const timer = window.setTimeout(stop, FOLLOW_MS);
    return () => { stop(); window.clearTimeout(timer); };
  }, [id, arrived, ready]);
}
